/**
 * Challonge-like Round Robin Engine
 * Deterministic schedule, results → standings, tie-breakers, top cut seeding.
 */

// Default configuration
export const getDefaultConfig = () => ({
  format: 'round_robin',
  roundRobin: {
    type: 'single', // single | double
    groups: 1,
    scheduleMethod: 'circle'
  },
  scoring: {
    winPoints: 3,
    drawPoints: 1,
    lossPoints: 0,
    byePoints: 3,
    byeAffectsTiebreakers: false
  },
  tieBreakers: [
    'matchPoints',
    'headToHead',
    'buchholz',
    'opponentMatchWinPct',
    'gameWinPct',
    'seed',
    'participantId'
  ],
  topCut: {
    enabled: false,
    size: 4,
    from: 'overall',
    seeding: 'standard',
    bracketType: 'single_elim'
  }
});

// Utility: deterministic group assignment (by seed or input order)
const assignGroups = (participants, groupCount) => {
  const groups = Array.from({ length: groupCount }, () => []);
  // deterministic: spread by index
  participants.forEach((p, idx) => {
    const g = idx % groupCount;
    groups[g].push({ ...p, groupId: `G${g + 1}` });
  });
  return groups;
};

// Utility: circle method schedule for one group
const generateGroupSchedule = (participants, groupId, type = 'single') => {
  const players = [...participants];
  const includeBye = players.length % 2 !== 0;
  if (includeBye) players.push({ id: 'BYE', name: 'BYE', seed: 999999 });

  const n = players.length;
  const roundsPerCycle = n - 1;
  const cycles = type === 'double' ? 2 : 1;
  const matches = [];

  for (let cycle = 0; cycle < cycles; cycle++) {
    // rotation list excluding the first fixed player
    const fixed = players[0];
    const rot = players.slice(1);
    for (let r = 0; r < roundsPerCycle; r++) {
      const roundNumber = cycle * roundsPerCycle + r + 1;
      const roundMatches = [];
      const left = [fixed, ...rot.slice(0, rot.length / 2)];
      const right = rot.slice(rot.length / 2).reverse();

      for (let i = 0; i < left.length; i++) {
        const A = left[i];
        const B = right[i];
        if (!A || !B) continue;
        // Home/away balance: alternate by roundNumber
        const isEven = roundNumber % 2 === 0;
        const pair = isEven ? [B, A] : [A, B];
        roundMatches.push({
          id: `${groupId}-R${roundNumber}-M${i + 1}-C${cycle + 1}`,
          stage: 'round_robin',
          groupId,
          roundNumber,
          tableNumber: i + 1,
          participantAId: pair[0].id,
          participantBId: pair[1].id,
          status: 'scheduled',
          result: null,
          audit: {}
        });
      }

      matches.push(...roundMatches);

      // rotate for next round (standard circle method)
      const last = rot.pop();
      rot.unshift(last);
    }
  }

  return matches;
};

// Apply a result to a match (pure, returns new match object)
export const applyResult = (match, outcome, scoreA = 0, scoreB = 0, config = getDefaultConfig()) => {
  const { scoring } = config;
  let pointsA = 0;
  let pointsB = 0;
  let status = 'completed';

  switch (outcome) {
    case 'A_WIN':
      pointsA = scoring.winPoints;
      pointsB = scoring.lossPoints;
      break;
    case 'B_WIN':
      pointsA = scoring.lossPoints;
      pointsB = scoring.winPoints;
      break;
    case 'DRAW':
      pointsA = scoring.drawPoints;
      pointsB = scoring.drawPoints;
      break;
    case 'BYE':
      pointsA = scoring.byePoints;
      pointsB = scoring.lossPoints;
      status = 'completed';
      break;
    default:
      status = 'scheduled';
  }

  return {
    ...match,
    status,
    result: {
      outcome,
      scoreA,
      scoreB,
      pointsA,
      pointsB,
      completedAt: status === 'completed' ? Date.now() : null
    }
  };
};

// Clear result
export const clearResult = (match) => ({
  ...match,
  status: 'scheduled',
  result: null
});

// Generate round robin matches for all groups
export const generateRoundRobinMatches = (participants, config = getDefaultConfig()) => {
  const groupsCount = Math.max(1, config.roundRobin?.groups || 1);
  const groups = assignGroups(participants, groupsCount);
  const matches = [];

  groups.forEach((group, idx) => {
    const groupId = `G${idx + 1}`;
    const schedule = generateGroupSchedule(group, groupId, config.roundRobin?.type || 'single');
    matches.push(...schedule);
  });

  // Auto-complete BYE matches
  const finalMatches = matches.map((m) => {
    if (m.participantBId === 'BYE' || m.participantAId === 'BYE') {
      return applyResult(m, 'BYE', 0, 0, config);
    }
    return m;
  });

  return { matches: finalMatches, groups };
};

// Compute standings from matches (pure)
export const computeStandings = (participants, matches, config = getDefaultConfig()) => {
  const { scoring, tieBreakers } = config;

  // initialize rows only for participants present
  const rows = new Map();
  participants.forEach((p) => {
    rows.set(p.id, {
      participantId: p.id,
      name: p.name || p.pseudo || p.id,
      seed: p.seed ?? null,
      matchesPlayed: 0,
      wins: 0,
      draws: 0,
      losses: 0,
      byes: 0,
      matchPoints: 0,
      opponentsPlayed: [],
      tieBreakValues: {},
      gameWins: 0,
      gameLosses: 0,
      gameDraws: 0
    });
  });

  // aggregate from completed matches
  matches.forEach((m) => {
    if (!m.result || m.status !== 'completed') return;
    const { participantAId, participantBId, result } = m;
    const rowA = rows.get(participantAId);
    const rowB = rows.get(participantBId);

    const isBye = participantAId === 'BYE' || participantBId === 'BYE';
    const validA = rowA && participantAId !== 'BYE';
    const validB = rowB && participantBId !== 'BYE';

    if (validA) rowA.matchesPlayed += 1;
    if (validB) rowB.matchesPlayed += 1;

    if (validA && validB) {
      rowA.opponentsPlayed.push(participantBId);
      rowB.opponentsPlayed.push(participantAId);
    }

    // update per outcome
    const { outcome, scoreA = 0, scoreB = 0, pointsA = 0, pointsB = 0 } = result;

    if (outcome === 'A_WIN') {
      if (validA) rowA.wins += 1;
      if (validB) rowB.losses += 1;
    } else if (outcome === 'B_WIN') {
      if (validB) rowB.wins += 1;
      if (validA) rowA.losses += 1;
    } else if (outcome === 'DRAW') {
      if (validA) rowA.draws += 1;
      if (validB) rowB.draws += 1;
    } else if (outcome === 'BYE') {
      if (validA && !validB) {
        rowA.byes += 1;
        rowA.wins += 1; // count as win
      }
      if (validB && !validA) {
        rowB.byes += 1;
        rowB.wins += 1;
      }
    }

    if (validA) {
      rowA.matchPoints += pointsA;
      rowA.gameWins += Number(scoreA) || 0;
      rowA.gameLosses += Number(scoreB) || 0;
    }
    if (validB) {
      rowB.matchPoints += pointsB;
      rowB.gameWins += Number(scoreB) || 0;
      rowB.gameLosses += Number(scoreA) || 0;
    }
  });

  // head-to-head helper
  const getHeadToHeadPoints = (aId, bId) => {
    let ptsA = 0;
    let ptsB = 0;
    matches.forEach((m) => {
      if (!m.result || m.status !== 'completed') return;
      const involvesA = m.participantAId === aId || m.participantBId === aId;
      const involvesB = m.participantAId === bId || m.participantBId === bId;
      if (involvesA && involvesB) {
        if (m.result.outcome === 'A_WIN') {
          ptsA += m.participantAId === aId ? scoring.winPoints : scoring.lossPoints;
          ptsB += m.participantBId === bId ? scoring.winPoints : scoring.lossPoints;
        } else if (m.result.outcome === 'B_WIN') {
          ptsA += m.participantBId === aId ? scoring.winPoints : scoring.lossPoints;
          ptsB += m.participantAId === bId ? scoring.winPoints : scoring.lossPoints;
        } else if (m.result.outcome === 'DRAW') {
          ptsA += scoring.drawPoints;
          ptsB += scoring.drawPoints;
        }
      }
    });
    return { ptsA, ptsB };
  };

  // Compute derived tie-breakers after base data
  const rowsArray = Array.from(rows.values());

  // helper maps
  const matchPointsMap = new Map(rowsArray.map((r) => [r.participantId, r.matchPoints]));
  const opponentsMap = new Map(rowsArray.map((r) => [r.participantId, r.opponentsPlayed]));

  // Buchholz: sum of opponents' match points (exclude BYE)
  rowsArray.forEach((r) => {
    const opps = opponentsMap.get(r.participantId) || [];
    const sum = opps.reduce((acc, oppId) => acc + (matchPointsMap.get(oppId) || 0), 0);
    r.tieBreakValues.buchholz = sum;
  });

  // Opponent Match Win Percentage (average)
  rowsArray.forEach((r) => {
    const opps = opponentsMap.get(r.participantId) || [];
    if (opps.length === 0) {
      r.tieBreakValues.opponentMatchWinPct = 0;
      return;
    }
    const sumPct = opps.reduce((acc, oppId) => {
      const opp = rows.get(oppId);
      if (!opp) return acc;
      const pct = opp.matchesPlayed > 0 ? (opp.wins + 0.5 * opp.draws) / opp.matchesPlayed : 0;
      return acc + pct;
    }, 0);
    r.tieBreakValues.opponentMatchWinPct = sumPct / opps.length;
  });

  // Game Win Percentage (if scores exist)
  rowsArray.forEach((r) => {
    const totalGames = r.gameWins + r.gameLosses + r.gameDraws;
    r.tieBreakValues.gameWinPct = totalGames > 0 ? r.gameWins / totalGames : 0;
  });

  // Head-to-head mini-league points map
  const headToHeadCache = new Map();
  const getHeadToHeadScore = (aId, bId) => {
    const key = `${aId}|${bId}`;
    if (headToHeadCache.has(key)) return headToHeadCache.get(key);
    const { ptsA, ptsB } = getHeadToHeadPoints(aId, bId);
    headToHeadCache.set(key, { ptsA, ptsB });
    return { ptsA, ptsB };
  };

  // Sorting with deterministic tie-breaks
  const comparator = (a, b) => {
    for (const tb of tieBreakers) {
      if (tb === 'matchPoints') {
        if (b.matchPoints !== a.matchPoints) return b.matchPoints - a.matchPoints;
      } else if (tb === 'headToHead') {
        // two-way only; multi-way handled by next tie-breakers
        const { ptsA, ptsB } = getHeadToHeadScore(a.participantId, b.participantId);
        if (ptsA !== ptsB) return ptsB - ptsA; // higher points wins
      } else if (tb === 'buchholz') {
        const diff = b.tieBreakValues.buchholz - a.tieBreakValues.buchholz;
        if (diff !== 0) return diff;
      } else if (tb === 'opponentMatchWinPct') {
        const diff = b.tieBreakValues.opponentMatchWinPct - a.tieBreakValues.opponentMatchWinPct;
        if (diff !== 0) return diff;
      } else if (tb === 'gameWinPct') {
        const diff = b.tieBreakValues.gameWinPct - a.tieBreakValues.gameWinPct;
        if (diff !== 0) return diff;
      } else if (tb === 'seed') {
        const aSeed = a.seed ?? Number.MAX_SAFE_INTEGER;
        const bSeed = b.seed ?? Number.MAX_SAFE_INTEGER;
        if (aSeed !== bSeed) return aSeed - bSeed;
      } else if (tb === 'participantId') {
        if (a.participantId < b.participantId) return -1;
        if (a.participantId > b.participantId) return 1;
      }
    }
    return 0;
  };

  rowsArray.sort(comparator);

  // assign ranks
  rowsArray.forEach((r, idx) => {
    r.rank = idx + 1;
  });

  return rowsArray;
};

// Explain standings (basic trace of tie-break values)
export const explainStandings = (participants, matches, config = getDefaultConfig()) => {
  const standings = computeStandings(participants, matches, config);
  return standings.map((r) => ({
    participantId: r.participantId,
    rank: r.rank,
    matchPoints: r.matchPoints,
    buchholz: r.tieBreakValues.buchholz,
    opponentMatchWinPct: r.tieBreakValues.opponentMatchWinPct,
    gameWinPct: r.tieBreakValues.gameWinPct,
    seed: r.seed ?? null,
    opponentsPlayed: r.opponentsPlayed
  }));
};

// Top cut seeding (standard 1vK)
export const generateTopCut = (standings, config = getDefaultConfig()) => {
  const { enabled, size } = config.topCut || {};
  if (!enabled || !size) return [];
  const top = standings.slice(0, size);
  const matches = [];
  for (let i = 0; i < size / 2; i++) {
    matches.push({
      id: `TOP-${size}-M${i + 1}`,
      stage: 'top_cut',
      roundNumber: 1,
      participantAId: top[i].participantId,
      participantBId: top[size - 1 - i].participantId,
      status: 'scheduled',
      result: null
    });
  }
  return matches;
};

// Convenience: create tournament (pure, returns schedule + initial standings)
export const createTournament = (participants, config = getDefaultConfig()) => {
  const { matches, groups } = generateRoundRobinMatches(participants, config);
  const standingsByGroup = groups.map((group, idx) => ({
    groupId: `G${idx + 1}`,
    standings: computeStandings(group, matches.filter(m => m.groupId === `G${idx + 1}`), config)
  }));
  return { matches, groups, standingsByGroup };
};
